import {
  mergeRight,
  mergeAll,
  isEmpty,
  clone,
  isNil,
  pick,
  not,
  find,
  propEq,
  compose,
  map,
  filter,
} from "ramda";
import { parseBool, camelCase } from "../string";
import {
  statusMapParser,
  UNKNOWN_STATUS,
  advancementTypes,
  IN_PROGRESS,
  RETIRED_AWARDS,
} from "../constants/advancements";
import {
  PROGRAM_CODES_BY_ID,
  OLD_PROGRAM_NAMES_ID_MAP,
  PROGRAM_ID_BY_CODES,
  UNKNOWN_PROGRAM,
  ALL_PROGRAMS,
} from "../constants/programs";
import {
  generateRequirementTree,
  transformRequirements,
} from "../requirements";
import { progressFromPercent } from "./progress";

const { MERIT_BADGE, ADVENTURE, ELECTIVE, AWARD, RANK } = advancementTypes;

function transformAward(award, isFromUserRecord) {
  const { program, programId } = getProgram(award);
  const userRelated = {};
  if (isFromUserRecord) {
    userRelated.userAwardId = parseInt(award.userAwardId, 10);
  }
  const id = getId(AWARD, award);
  return {
    ...award,
    ...userRelated,
    awardId: id,
    program,
    programId,
    rank: camelCase(award.rank),
    isRetired: isAwardRetired(id),
  };
}

function transformMeritBadge(meritBadge) {
  return {
    ...pick(["categoryId", "category", "versions"], meritBadge),
    counselorId: meritBadge.assignedCounselorUserId || null,
    counselorDetails: meritBadge.assignedCounselorUser || {},
    eagleRequired: parseBool(meritBadge.eagleRequired),
  };
}

function transformAdventure(adventure, ranks = {}) {
  const version = parseInt(adventure.version, 10);
  const name = adventure.name || adventure.adventureName;
  const isRequired = parseBool(adventure.required) || adventure.isRequired;
  const rank = find(propEq("id", adventure.rankId.toString()))(ranks);
  return {
    ...adventure,
    name,
    isRequired,
    rank: camelCase(rank.name),
    required: isRequired,
    category: reviewRequired(name, version, isRequired),
    versionId: parseInt(adventure.versionId, 10),
    version: String(adventure.version),
  };
}

function transformRank(rank, isFromUserRecord) {
  //  program and programId
  // versionId and version
  const { program, programId } = getProgram(rank);
  const version = parseInt(rank.version, 10);
  return {
    ...pick(["footer", "header", "short"], rank),
    program,
    programId,
    isInYouthRecord: isFromUserRecord,
    leaderApprovedUserId: rank.leaderApprovedUserId,
    level: parseInt(rank.level, 10),
    versionId: parseInt(rank.versionId, 10),
    version,
  };
}

function transformElective(elective) {
  return {
    id: elective.ssElectiveId,
    name: elective.ssElectiveName || elective.ssName,
    ssElectiveName: elective.ssElectiveName,
    ssName: elective.ssName,
    ssLevel: elective.ssLevel,
    status: IN_PROGRESS,
  };
}

// AUXILIAR FUNCTIONS FOR SPECIFIC TRANSFORMATIONS
function programIsAll(program) {
  return isNil(program) || program.toLowerCase() === "all";
}
function getProgram(advancement) {
  const { programId: _id, program: _program } = advancement;
  let programId = _id;
  if (!programId) {
    programId =
      OLD_PROGRAM_NAMES_ID_MAP[_program] ||
      PROGRAM_ID_BY_CODES[camelCase(_program)];
  }
  const program =
    PROGRAM_CODES_BY_ID[programId] ||
    (programIsAll(_program) && ALL_PROGRAMS) ||
    UNKNOWN_PROGRAM;
  return {
    program,
    programId,
  };
}

function isAwardRetired(id) {
  return RETIRED_AWARDS.includes(id);
}

const forceElective = { "Grin and Bear It": true };
const forceRequired = { "Baloo the Builder": true };
const reviewRequired = (name, version, required) => {
  if (forceRequired[name] && version >= 2016) {
    return "required";
  }

  if (forceElective[name] && version >= 2016) {
    return "elective";
  }

  return required ? "required" : "elective";
};

// AUXILIAR FUNCTIONS FOR COMMON TRANSFORMATIONS
export function parseStatus(statusRawValue) {
  const _statusRaw = String(statusRawValue).toLowerCase();
  const transformedStatus = statusMapParser[_statusRaw];
  return transformedStatus || UNKNOWN_STATUS;
}

export function parseRequirementStatus(requirement) {
  const statusRawValue = String(requirement.status).toLowerCase();
  const transformedStatus = statusMapParser[statusRawValue];
  return transformedStatus || UNKNOWN_STATUS;
}

function getIsAwarded(advancement) {
  return parseBool(advancement.awarded) || false;
}
function getIsCompleted(advancement) {
  return parseBool(advancement.completed) || false;
}

function __valueOr(_default, keys, advancement) {
  const obj = {};
  keys.forEach((key) => {
    obj[key] = advancement[key] || _default;
  });
  return obj;
}

const propIdSelector = {
  [AWARD]: "awardId",
  [ELECTIVE]: "ssElectiveId",
  [ADVENTURE]: "adventureId",
};

function getId(type, advancement) {
  const propId = propIdSelector[type];
  return advancement.id || advancement[propId];
}

function commonTransformations(type, advancement) {
  const progress = progressFromPercent(advancement.percentCompleted);
  const id = parseInt(getId(type, advancement), 10);
  const status = parseStatus(advancement.status);
  const isAwarded = getIsAwarded(advancement);
  const isCompleted = getIsCompleted(advancement);
  const valuesOrEmpty = __valueOr(
    "",
    [
      "dateStarted",
      "dateEarned",
      "dateCompleted",
      "dateAwarded",
      "markedCompletedDate",
      "leaderApprovedDate",
      "lastUpdated",
      "shortName",
      "description",
      "bsaNumber",
      "name",
      "sku",
    ],
    advancement
  );
  return {
    ...valuesOrEmpty,
    id,
    status,
    progress,
    isAwarded,
    isCompleted,
    requirements: advancement.requirements || [],
    image: advancement.image || advancement.imageUrl100,
    thumbnail: advancement.imageUrl100,
  };
}

const transformMap = {
  [AWARD]: transformAward,
  [MERIT_BADGE]: transformMeritBadge,
  [ADVENTURE]: transformAdventure,
  [RANK]: transformRank,
  [ELECTIVE]: transformElective,
};

export function getRequirementsTree(requirements = []) {
  const transformedRequirements = transformRequirements(
    requirements,
    parseRequirementStatus
  );
  return generateRequirementTree(transformedRequirements);
}

export function transformAdvancement(type, advancementObj, isFromUserRecord) {
  const transformer = transformMap[type];
  if (transformer) {
    const commonlyTransformed = commonTransformations(type, advancementObj);
    const advancement = mergeRight(advancementObj, commonlyTransformed);
    const transformedProps = transformer(advancement, isFromUserRecord);
    const { requirements } = commonlyTransformed;
    return mergeAll([
      commonlyTransformed,
      transformedProps,
      { requirements: getRequirementsTree(requirements) },
    ]);
  }
  return null;
}

export function transformAdvancementArray(
  type,
  advancements,
  requirements = [],
  isFromUserRecord = false
) {
  return advancements.map((_advancement) => {
    const advancement = clone(_advancement);
    const id = getId(type, advancement);
    if (not(isEmpty(requirements)) && requirements[id]) {
      advancement.requirements = requirements[id];
    }
    const adv = transformAdvancement(type, advancement, isFromUserRecord);
    return adv;
  });
}

export const transformAdvancementRequirementsForUpdate = (requirements = {}) =>
  compose(
    // transform requirements for update
    map((requirement) => {
      const completed = parseBool(requirement.completed);
      const started = parseBool(requirement.started);
      const approved = parseBool(requirement.approved);
      const transformedRequirement = {
        id: parseInt(requirement.id, 10),
        completed,
        started,
        approved,
      };
      if (completed) {
        transformedRequirement.dateCompleted = requirement.dateCompleted;
        transformedRequirement.dateStarted = requirement.dateCompleted;
        transformedRequirement.markedCompletedDate =
          requirement.markedCompletedDate;
      }
      if (requirement.leaderApprovedUserId) {
        transformedRequirement.leaderApprovedDate =
          requirement.leaderApprovedDate;
        transformedRequirement.leaderApprovedUserId =
          requirement.leaderApprovedUserId;
      }
      return transformedRequirement;
    }),
    // Filter parent requirements (API will update them)
    filter(
      ({ childrenRequired }) =>
        childrenRequired === 0 || isEmpty(childrenRequired)
    )
  )(Object.values(requirements));

export const transformSSElectiveForUpdate = (requirements = {}) =>
  // transform requirements for update
  map((requirement) => {
    const completed = parseBool(requirement.completed);
    const transformedRequirement = {
      requirementId: parseInt(requirement.id, 10),
    };
    if (completed) {
      transformedRequirement.dateCompleted = requirement.dateCompleted;
      transformedRequirement.markedCompletedUserId =
        requirement.leaderApprovedUserId;
      if (requirement.leaderApprovedUserId) {
        transformedRequirement.leaderApprovedDate =
          requirement.leaderApprovedDate;
        transformedRequirement.leaderApprovedUserId =
          requirement.leaderApprovedUserId;
      } else {
        transformedRequirement.leaderApprovedDate = null;
        transformedRequirement.leaderApprovedUserId = null;
      }
    } else {
      transformedRequirement.dateCompleted = null;
      transformedRequirement.markedCompletedUserId = null;
      transformedRequirement.leaderApprovedDate = null;
      transformedRequirement.leaderApprovedUserId = null;
    }
    return transformedRequirement;
  }, Object.values(requirements));

export default transformAdvancement;
